import { useState, useEffect } from 'react'
import * as con from '../../GlobalConstants.js'
import { GenericCell } from '../../templates/tables/Cells.js'
import { translateTermRange, translateView } from '../../utils/translateFunctions.js'
import { Checkbox } from '@blueprintjs/core';
import { RefModelParameterHook, StateModelParameterHook } from '../hooks/ModelParameterHooks.js'
import { RefGridStrategyHook, StateGridStrategyHook } from '../hooks/gridStrategyHooks.js'
import { Icon } from '@blueprintjs/core';
import { useDispatch } from 'react-redux';
import { RefSPOT } from '../hooks/MarketValuesHook'
import { fetchGridStrategy } from '../../store/slices/gridSlice.js';
import { translateTargetBand } from './GridParams.js';
import GridWarningsModal from './GridWarningsModal.js'
import LoadingOverlay from '../modales/LoadingOverlay.js'
import Modal from '../../templates/popups/Modal.js'
import { makeGridLadderPptx } from '../../utils/reportFunctions.js'

const GridMarketSummary = () => {
  const dispatch = useDispatch();
  const [loadingStrategy, setLoadingStrategy] = useState(false)
  const [printingLadder, setPrintingLadder] = useState(false)
  const [strategyGenerated, setStrategyGenerated] = useState(false)
  const gridStrategy = RefGridStrategyHook(con.GRID_STRATEGY)
  const clientType   = RefModelParameterHook(con.CLIENT_TYPE)
  const weeksToWait  = RefModelParameterHook(con.WEEKS_TO_WAIT)

  const [selectedGridView, setSelectedGridView]   = StateGridStrategyHook(con.GRID_VIEW);
  const [referenceValue,   setReferenceValue]     = StateGridStrategyHook(con.GRID_REFERENCE_VALUE)
  const [gridTerm,         setGridTerm]           = StateModelParameterHook(con.GRID_TERM)
  const manualOverrides                           = RefGridStrategyHook(con.MANUAL_OVERRIDES)
  const [manual,           setManual]             = useState(false)
  const [warnings,         setWarnings]           = useState([])
  const [showWarningsModal, setShowWarningsModal] = useState(false)
  const [infoWarnings,     setInfoWarnings]       = useState([])
  const [showInfoModal,    setShowInfoModal]       = useState(false)
  const volatility                                = Number((RefGridStrategyHook(con.VOLATILITY) ?? 0).toFixed(2))
  const errorType                                 = RefGridStrategyHook(con.ERROR_TYPE)
  const [hasEmptyExposures, setHasEmptyExposures] = useState(false)
  const isBlocked                                 = hasEmptyExposures || errorType === "empty_exposures"
  const [showEmptyExposuresModal, setShowEmptyExposuresModal] = useState(false)

  const strategy_speed           = RefModelParameterHook(con.STRATEGY_SPEED)
  const [targetBand, setTargetBand] = StateGridStrategyHook(con.TARGET_BAND)
  const upper_bound              = RefGridStrategyHook(con.UPPER_BOUND)
  const lower_bound              = RefGridStrategyHook(con.LOWER_BOUND)
  const user_line                = RefModelParameterHook(con.USER_LINE)
  const can_spot                 = RefModelParameterHook(con.CAN_SPOT)
  const can_fwds                 = RefModelParameterHook(con.CAN_FWDS)
  const can_options              = RefModelParameterHook(con.CAN_OPTIONS)
  const considered_spot_coverage = RefModelParameterHook(con.CONSIDERED_SPOT_COVERAGE)
  const automatic_grid           = RefModelParameterHook(con.AUTOMATIC_GRID)
  const reference_spot           = RefSPOT()

  useEffect(() => {
    setHasEmptyExposures(false)
    setStrategyGenerated(false)
  }, [user_line])

  useEffect(() => {
    if (errorType === "empty_exposures") {
      setShowEmptyExposuresModal(true)
    }
  }, [errorType])

  const INSTRUMENT_LABEL = { fwds: 'Forward', options: 'Collares', spot: 'Spot' }

  const renderWarningText = (w) => {
    if (w[con.TYPE] === 'info') {
      const pos    = ((w.current_position ?? 0) * 100).toFixed(1)
      const target = ((w[con.TARGET]         ?? 0) * 100).toFixed(1)
      return `El cliente ya alcanzó o superó la banda objetivo. Posición actual: ${pos}%, objetivo: ${target}%.`
    }
    if (w[con.TYPE] === 'monotony' && w[con.FREQUENCY] != null) {
      const inst = INSTRUMENT_LABEL[w[con.INSTRUMENT]] ?? w[con.INSTRUMENT]
      return `La frecuencia manual del escalón ${w[con.STEP]} (${inst}) viola la regla de frecuencias no crecientes entre escalones (frecuencia actual: ${Number(w[con.FREQUENCY]).toFixed(0)}).`
    }
    if (w[con.TYPE] === 'monotony') {
      const inst = INSTRUMENT_LABEL[w[con.INSTRUMENT]] ?? w[con.INSTRUMENT]
      return `Los montos del instrumento ${inst} no cumplen la regla de no decrecimiento entre escalones.`
    }
    return JSON.stringify(w)
  }

  const buildPayload = () => {
    const payload = {
      [con.USER_LINE]                : user_line,
      [con.GRID_REFERENCE_VALUE]     : referenceValue,
      "reference_spot"               : reference_spot,
      [con.GRID_TERM]                : gridTerm,
      [con.STRATEGY_SPEED]           : strategy_speed,
      [con.CAN_SPOT]                 : can_spot,
      [con.CONSIDERED_SPOT_COVERAGE] : considered_spot_coverage,
      [con.CAN_FWDS]                 : can_fwds,
      [con.CAN_OPTIONS]              : can_options,
      [con.GRID_VIEW]                : selectedGridView,
      [con.AUTOMATIC_GRID]           : automatic_grid,
      [con.TARGET]                   : targetBand,
    }

    if (!automatic_grid) {
      // Send ONLY the cells the operator actually edited. Sending the whole
      // grid (previous behavior) pinned every step/instrument to the last
      // response and masked manual frequencies behind their amounts.
      for (const [cellKey, override] of Object.entries(manualOverrides ?? {})) {
        const value = override?.value  // already a number stored by setManualOverride
        if (value == null) continue

        const [step, inst] = cellKey.split('.')
        const payloadKey = override.field === con.FREQUENCY
          ? `${step}_frequency_${inst}`
          : `${step}_${inst}_amount`

        payload[payloadKey] = value
      }
    }

    return payload
  }

  const handleGenerateGrid = async () => {
    try {
      setLoadingStrategy(true)
      const payload  = buildPayload()
      const response = await dispatch(fetchGridStrategy(payload)).unwrap()
      const errorWarning = response[con.WARNINGS]?.find(w => w[con.TYPE] === "error")
      if (errorWarning) {
        setHasEmptyExposures(true)
        setShowEmptyExposuresModal(true)
        return
      }

      setStrategyGenerated(true)

      const allWarnings     = response[con.WARNINGS] ?? []
      const decisionWarnings = allWarnings.filter(w => w[con.TYPE] === "decision")
      const textWarnings     = allWarnings.filter(w => w[con.TYPE] === "info" || w[con.TYPE] === "monotony")

      if (decisionWarnings.length > 0) {
        setWarnings(decisionWarnings)
        setShowWarningsModal(true)
      }
      if (textWarnings.length > 0) {
        setInfoWarnings(textWarnings)
        setShowInfoModal(true)
      }
    } catch (err) {
      console.error('Error al generar grid:', err)
    } finally {
      setLoadingStrategy(false)
    }
  }

  const handlePrintLadder = async () => {
    try {
      setPrintingLadder(true)
      await dispatch(makeGridLadderPptx({ gridStrategy, clientType, weeksToWait, referenceValue })).unwrap()
    } catch (err) {
      console.error('Error al imprimir la escalera:', err)
    } finally {
      setPrintingLadder(false)
    }
  }

  const handleAcceptWarnings = () => {
    setShowWarningsModal(false)
    setWarnings([])
  }

  const handleEditWarnings = async (editedBullets) => {
    try {
      const payload = buildPayload()
      payload[con.AUTOMATIC_GRID] = false

      for (const [key, value] of Object.entries(editedBullets)) {
        payload[`step_${key}_amount`] = value
      }

      const response = await dispatch(fetchGridStrategy(payload)).unwrap()

      if (response[con.WARNINGS]?.length > 0) {
        setWarnings(response[con.WARNINGS])
      } else {
        setShowWarningsModal(false)
        setWarnings([])
      }
    } catch (err) {
      console.error('Error al generar grid con edición:', err)
    }
  }

  return (
    <>
      {loadingStrategy && <LoadingOverlay />}
      {showWarningsModal && (
        <GridWarningsModal
          warnings={warnings}
          onAccept={handleAcceptWarnings}
          onEdit={handleEditWarnings}
        />
      )}

      <Modal
          show={showEmptyExposuresModal}
          doAccept={null}
          doCancel={() => setShowEmptyExposuresModal(false)}
          showAccept={false}
          widthPercentage={35}
      >
          <h4>Sin exposiciones</h4>
          <p>Este cliente no tiene exposiciones registradas.</p>
      </Modal>

      <Modal
          show={showInfoModal}
          doAccept={() => setShowInfoModal(false)}
          doCancel={() => setShowInfoModal(false)}
          showCancel={false}
          widthPercentage={40}
      >
          <h4>Advertencias</h4>
          <ul style={{ marginTop: "12px", paddingLeft: "16px" }}>
            {infoWarnings.map((w, i) => (
              <li key={i} style={{ marginBottom: "10px", fontSize: "13px", lineHeight: "1.5" }}>
                {renderWarningText(w)}
              </li>
            ))}
          </ul>
      </Modal>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.4rem", padding: "15px 12px" }}>
        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
          <button
            className="control-button grey"
            onClick={handleGenerateGrid}
            disabled={isBlocked}
            style={{
              fontSize: "14px",
              padding: "6px 12px",
              fontWeight: "bold",
              backgroundColor: isBlocked ? "#aaa" : "var(--background-color-3)",
              color: "white",
              cursor: isBlocked ? "not-allowed" : "pointer"
            }}
          >
            <Icon icon="refresh" style={{ marginRight: "4px" }} />
            GENERAR
          </button>

          <button
            className="control-button grey"
            style={{ fontSize: "14px", padding: "6px 12px", fontWeight: "bold", backgroundColor: "var(--background-color-3)", color: "white" }}
            onClick={() => console.log("Guardar")}
          >
            <Icon icon="floppy-disk" style={{ marginRight: "4px" }} />
            GUARDAR
          </button>

          <button
            className="control-button grey"
            title={!strategyGenerated ? "Dale a GENERAR antes de imprimir" : undefined}
            style={{ fontSize: "14px", padding: "6px 12px", fontWeight: "bold", backgroundColor: (printingLadder || !strategyGenerated) ? "#aaa" : "var(--background-color-3)", color: "white", cursor: (printingLadder || !strategyGenerated) ? "not-allowed" : "pointer" }}
            onClick={handlePrintLadder}
            disabled={printingLadder || !strategyGenerated || !gridStrategy}
          >
            <Icon icon="download" style={{ marginRight: "4px" }} />
            IMPRIMIR
          </button>
        </div>

        {!strategyGenerated && (
          <div style={{ fontSize: "12px", color: "#888" }}>
            Dale a GENERAR antes de imprimir
          </div>
        )}
      </div>

      <div style={{ marginBottom: "1rem", fontWeight: "bolder" }}>
        DATOS DE MERCADO
      </div>

      <br />
      <div className="flex-fila nonFixedTable">
        <div className="flex-celda header-row flex items-center">VIEW</div>
        <div className="flex-celda header-row">BANDA OBJETIVO</div>
      </div>
      <div className="flex-fila nonFixedTable">
        <div className="flex-celda nonFixedTable">
          <GenericCell
            evenRow
            valueTranslator={translateView}
            editing
            cellType={con.MULTIPLE_SELECTION}
            options={con.GRID_AVAILABLE_VIEWS}
            value={selectedGridView}
            setValue={setSelectedGridView}
          />
          <GenericCell
            valueTranslator={translateTargetBand}
            evenRow
            value={targetBand}
            setValue={(val) => setTargetBand(Number(val))}
            cellType={con.MULTIPLE_SELECTION}
            options={con.TARGET_BAND_OPTIONS.map((band) => band.value)}
            editing
          />
        </div>
      </div>

      <br />
      <div className="flex-fila nonFixedTable">
        <div className="flex-celda header-row flex items-center space-x-2" style={{ flex: '1 1 100%' }}>
          VLR. REFERENCIA
        </div>
      </div>

      <div className="flex-fila nonFixedTable">
        <div className="flex-fila nonFixedTable">
          <label className="flex-celda even-row flex items-center space-x-2">
            <Checkbox
              checked={manual}
              onChange={(e) => setManual(e.target.checked)}
              style={{ margin: "0" }}
            />
            Manual
          </label>
          <GenericCell
            evenRow
            cellType={con.MONEY}
            editing={manual}
            value={referenceValue}
            currency={con.MONEY_COP}
            setValue={(val) => {
              if (!manual) return
              setReferenceValue(Number(val) || 0)
            }}
          />
        </div>
      </div>

      <br />
      <div className="flex-fila nonFixedTable">
        <div className="flex-celda header-row">RANGO</div>
        <div className="flex-celda header-row">MAX</div>
        <div className="flex-celda header-row">MIN</div>
        <div className="flex-celda header-row">ATR</div>
      </div>

      <div className="flex-fila nonFixedTable">
        <GenericCell
          valueTranslator={translateTermRange}
          evenRow
          value={gridTerm}
          setValue={setGridTerm}
          cellType={con.MULTIPLE_SELECTION}
          editing
          options={con.GRID_AVAILABLE_TERM_TYPES}
        />
        <GenericCell
          evenRow
          cellType={con.MONEY}
          editing={false}
          value={upper_bound}
          currency={con.MONEY_COP}
        />
        <GenericCell
          evenRow
          cellType={con.MONEY}
          editing={false}
          value={lower_bound}
          currency={con.MONEY_COP}
        />
        <GenericCell
          evenRow
          cellType={con.NUMERIC}
          editing={false}
          value={volatility}
        />
      </div>
    </>
  )
}

export default GridMarketSummary